/**
 * Environment-aware logger utility
 * Only logs in development, silent in production
 *
 * Pass 8 root-cause fix: this module is bundled into the Workflow DevKit
 * runtime as CJS, where `import.meta` is undefined. Reading
 * `import.meta.env.DEV` directly threw "Cannot read properties of undefined
 * (reading 'DEV')" which crashed every Veo workflow at line 1 before any
 * step ran. Now wrapped in try/catch with a process.env fallback so the
 * module loads safely in BOTH browser (ESM, import.meta defined) AND
 * server/workflow (CJS, import.meta empty).
 */

const isDevelopment = (() => {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV !== undefined) {
      return Boolean((import.meta as any).env.DEV);
    }
  } catch {
    // import.meta unsupported in this runtime — fall through
  }
  return process.env.NODE_ENV !== 'production';
})();

const isDebugEnabled = (() => {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_DEBUG !== undefined) {
      return (import.meta as any).env.VITE_DEBUG === 'true';
    }
  } catch {
    // import.meta unsupported
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env.VITE_DEBUG === 'true';
  }
  return false;
})();

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix?: string;
}

class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      enabled: isDevelopment,
      level: 'info',
      ...config,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;

    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(message: string): string {
    if (this.config.prefix) {
      return `[${this.config.prefix}] ${message}`;
    }
    return message;
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage(message), ...args);
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog('info')) {
      console.log(this.formatMessage(message), ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(message), ...args);
    }
  }

  error(message: string, error?: any, ...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage(message), error, ...args);
    }
  }

  // Performance logging (only in debug mode)
  perf(operation: string, duration: number): void {
    if (isDebugEnabled && duration > 16) {
      // Log slow operations > 16ms
      console.warn(`[Performance] ${operation}: ${duration.toFixed(2)}ms`);
    }
  }

  // Group logging for better organization
  group(label: string): void {
    if (this.config.enabled) {
      console.group(this.formatMessage(label));
    }
  }

  groupEnd(): void {
    if (this.config.enabled) {
      console.groupEnd();
    }
  }
}

// Create default logger instance
export const logger = new Logger();

// Create specialized loggers
export const serviceLogger = new Logger({ prefix: 'Service' });
export const hookLogger = new Logger({ prefix: 'Hook' });
export const perfLogger = new Logger({ prefix: 'Perf', level: 'warn' });

// Helper to create custom loggers
export function createLogger(prefix: string, config?: Partial<LoggerConfig>): Logger {
  return new Logger({ prefix, ...config });
}

// Production-safe console override
export function setupProductionLogging(): void {
  if (!isDevelopment) {
    // In production, replace console methods with no-ops
    const noop = () => {};

    // Keep error and warn for critical issues
    console.log = noop;
    console.debug = noop;
    console.info = noop;

    // Optionally disable all console methods. Same CJS-safe import.meta
    // wrap as the module-level checks above.
    let disableAllLogs = false;
    try {
      if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
        disableAllLogs = (import.meta as any).env.VITE_DISABLE_ALL_LOGS === 'true';
      }
    } catch {
      // import.meta unsupported
    }
    if (disableAllLogs) {
      console.warn = noop;
      console.error = noop;
    }
  }
}
